import _ from 'underscore';

import { ProjectMapper } from '~src/projects/models/mappers/ProjectMapper';

import { appendDraftIdPrefix } from '../../../drafts/utils/DraftUtils';
import { ILocationInfo } from '../../../personalSpace/model/ILocationInfo';
import { IUsageInfo } from '../../../spacesPlatform/models/IUsageInfo';
import { ContentMapper } from '../../../spacesPlatform/models/mappers/ContentMapper';
import { MapperHelper } from '../../../spacesPlatform/models/mappers/MapperHelper';
import {INamedItemInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { IUserInfo } from '../../../userManagement/users/models/IUserInfo';
import { UserMapper } from '../../../userManagement/users/models/mappers/UserMapper';
import { FlowBasic, FlowDetail, FlowListItem } from '../../serverApi/types/Flow';
import { FlowBasicInfo, FlowDetailInfo, FlowListItemInfo } from '../FlowInfo';

export module FlowMapper {

  const M = MapperHelper;

  export function mapBasic(
    serverApiFlows: ReadonlyArray<FlowBasic>,
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo> = {},
  ): FlowBasicInfo[] {
    const flowsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.flow, projects);
    return _.map(serverApiFlows, (f: FlowBasic) => {
      const fbInfo: FlowBasicInfo = {
        id:                        f.id,
        luid:                      f.luid,
        name:                      f.name,
        owner:                     users[f.ownerId],
        project:                   flowsToProject[f.id],
        contentType:               'flow',
      };
      return fbInfo;
    });
  }

  export function map(
    flows: FlowListItem[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    locations: ILocationInfo[] = [],
    usageInfoMaxValues?: IUsageInfo,
    favoriteFlows: _.Dictionary<any> = {},
  ): FlowListItemInfo[] {
    const flowsToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.flow, projects);
    const flowsToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.flow, locations);
    return _.map(flows, (f: FlowListItem) =>
      mapOne(f, users[f.ownerId],flowsToProject[f.id],
        usageInfoMaxValues, !!favoriteFlows[f.id], flowsToLocation[f.id])
    );
  }

  export function mapOneForAlert(
    f: FlowBasic,
  ): FlowBasicInfo {
    return {
      id: f.id,
      luid: f.luid,
      name: f.name,
      contentType: 'flow',
    };
  }

  export function mapOne(
    f: FlowListItem,
    owner: IUserInfo,
    project: INamedItemInfo,
    usageInfoMaxValues?: IUsageInfo,
    favorite?: boolean,
    location?: ILocationInfo
  ): FlowListItemInfo {
    return {
      id:                    f.id ? f.id : appendDraftIdPrefix(f.draftId), // drafts with 'unpublished' status don't have an id so map to draftId with prefix for uniqueness
      luid:                  f.luid,
      inputCount:            f.inputCount,
      name:                  f.name,
      outputCount:           f.outputCount,
      owner:                 UserMapper.mapOne(owner),
      project:               project,
      location:              location,
      description:           f.description,
      updatedAt:             M.toDate(f.updatedAt),
      favorite:              favorite,
      tags:                  M.sortTags(f.tags),
      usageInfo:             f.usageInfo,
      usageInfoMaxValues:    usageInfoMaxValues || <IUsageInfo>{},
      contentType:           'flow',
      flowFileType:          f.fileType,
      flowDocumentVersion:   f.documentVersion,
      thumbnailUrl:          f.thumbnailUrl,
      dataQualityIndicators: f.dataQualityIndicators || [],
      draftStatus:           f.draftStatus,
      draftId:               f.draftId ? f.draftId : null,
      draftLuid:             f.draftLuid ? f.draftLuid : null,
      relevantParameters:    f.relevantParameters,
      containsParameters:    f.containsParameters,
    };
  }

  export function mapDetail(f: FlowDetail): FlowDetailInfo {
    return {
      id:                    f.id ? f.id : appendDraftIdPrefix(f.draftId), /// drafts with 'unpublished' status don't have an id so map to draftId with prefix for uniqueness
      inputCount:            f.inputCount,
      name:                  f.name,
      outputCount:           f.outputCount,
      createdAt:             M.toDate(f.createdAt),
      description:           f.description,
      updatedAt:             M.toDate(f.updatedAt),
      downloadUrl:           f.downloadUrl,
      editUrl:               f.editUrl,
      favorite:              f.favorite,
      tags:                  M.sortTags(f.tags),
      owner:                 UserMapper.mapOne(f.owner),
      project:               f.project ? ProjectMapper.mapName(f.project) : null,
      contentType:           'flow',
      flowGraphImageUrl:     f.flowGraphImageUrl,
      flowFileType:          f.fileType,
      flowDocumentVersion:   f.documentVersion,
      hasExtracts:           f.hasExtracts,
      hasEncryptableExtracts: f.hasEncryptableExtracts,
      extractEncryptionState: f.extractEncryptionState,
      dataQualityIndicators:  f.dataQualityIndicators || [],
      luid:                   f.luid,
      draftStatus:            f.draftStatus,
      flowDraft:              f.flowDraft,
      draftId:                f.draftId ? f.draftId : null,
      draftLuid:              f.draftLuid ? f.draftLuid : null,
      relevantParameters:     f.relevantParameters,
      containsParameters:     f.containsParameters,
    };
  }

  export function mapFromCRASS(content: any): FlowListItemInfo{
    return {
        id:                    content.id + '',
        luid:                  content.luid,
        inputCount:            0, //missing
        name:                  content.title,
        outputCount:           0, //missing
        project:                   {
          id: content.projectId ?? content.parentProjectId,
          name: content.containerName
        },
        description:           content.description,
        updatedAt:             M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime),
        favorite:              false, //missing
        tags:                  M.sortTags(content.tags),
        usageInfo:             null, //missing
        usageInfoMaxValues:    <IUsageInfo>{}, //missing
        contentType:           'flow',
        flowFileType:          content.fileType,
        flowDocumentVersion:   content.version,
        thumbnailUrl:          getFlowThumbnailUrl(content.id, (M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime))),
        dataQualityIndicators: [], //missing
        owner: UserMapper.mapOneOwnerFromCRASS(content),
        draftStatus:           content.draftStatus,
        draftId:               null, // TODO
        draftLuid:             null, // TODO
      };
  }

  function getFlowThumbnailUrl(flowId: String, updatedAt: Date){
    const cacheValue = updatedAt ? updatedAt.getTime() : '';
    return M.REST_API_BASE_URL + `flows/${flowId}/thumbnail?${cacheValue}`;
  }
}
